import { Link } from "react-router-dom";
import { ArrowUpRight, Check, MapPin, Phone } from "lucide-react";
import SEO from "./SEO";
import EditorialMarquee from "./EditorialMarquee";
import StatusBadge from "./StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "./Reveal";
import { IMG, PHONE_DISPLAY, PHONE_TEL, SERVICES, TARIFS } from "../data/site";

function seedIndex(seed, mod) {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    return h % mod;
}

function getGuaranteesCopy(name, v) {
    const variants = [
        {
            intro: `Dans le métier depuis 1985, notre équipe d'artisans serruriers intervient à ${name} et dans tous les secteurs de Paris et l'Île-de-France. Réactivité, transparence tarifaire et qualité d'exécution sont nos priorités absolues.`,
            honestyTitle: `Serrurier honnête à ${name} : comment éviter les arnaques ?`,
            honestyP: `Méfiez-vous des serruriers qui refusent de donner un tarif au téléphone, n'affichent pas leur numéro SIRET ou ne proposent pas de devis écrit avant de commencer les travaux. À ${name}, nos artisans agréés présentent systématiquement un devis détaillé avant toute intervention.`,
        },
        {
            intro: `Nos équipes connaissent bien le secteur de ${name} : chaque artisan est formé pour intervenir rapidement, sans dégrader votre porte ni votre serrure, et en respectant le prix annoncé au téléphone.`,
            honestyTitle: `Comment reconnaître un serrurier fiable à ${name} ?`,
            honestyP: `Un professionnel sérieux communique son tarif avant de se déplacer, présente sa carte professionnelle et remet une facture détaillée. À ${name}, c'est la règle que nous appliquons sur chaque intervention, sans exception ni frais caché.`,
        },
        {
            intro: `À ${name} comme partout dans notre zone d'intervention, nous appliquons les mêmes standards : diagnostic honnête, prix fixé avant travaux et matériel certifié posé dans les règles.`,
            honestyTitle: `Éviter les arnaques de serrurerie à ${name}`,
            honestyP: `Un tarif qui change une fois l'artisan sur place, l'absence de devis écrit ou un refus de justifier son SIRET sont des signaux d'alerte. Nos équipes à ${name} s'engagent sur un devis clair, validé avant toute intervention.`,
        },
    ];
    return variants[v % variants.length];
}

function getFaqCopy(name, v) {
    const variants = [
        [
            {
                q: `Quel est le délai d'intervention d'un serrurier à ${name} ?`,
                a: `Notre délai moyen d'intervention à ${name} est de 25 à 30 minutes après votre appel. Nos artisans sont positionnés dans les secteurs parisiens pour garantir ce délai, 24h/24 et 7j/7, week-ends et jours fériés inclus.`,
            },
            {
                q: `Combien coûte l'ouverture d'une porte à ${name} ?`,
                a: `L'ouverture d'une porte simplement claquée est facturée 60€ TTC à ${name}. Pour une porte fermée à clé ou une porte blindée, le tarif dépend du type de serrure et est communiqué lors d'un devis gratuit par téléphone avant toute intervention.`,
            },
            {
                q: `Intervenez-vous la nuit et les week-ends à ${name} ?`,
                a: `Oui, notre service de dépannage fonctionne 24h/24, 7j/7 à ${name}, y compris les nuits, les week-ends et tous les jours fériés. Le tarif de nuit peut varier — demandez une confirmation par téléphone.`,
            },
            {
                q: `Faut-il un justificatif de domicile pour un serrurier à ${name} ?`,
                a: `Oui, pour toute ouverture de porte ou changement de serrure à ${name}, notre artisan est tenu de demander un justificatif de domicile ou titre de propriété. C'est une mesure de sécurité obligatoire pour protéger les occupants légitimes.`,
            },
            {
                q: `Êtes-vous agréé assurance à ${name} ?`,
                a: `Oui, nous sommes agréés par les principales compagnies d'assurance habitation. Après notre intervention à ${name}, vous recevez une facture détaillée conforme aux exigences de votre assureur, avec les certificats de garantie sur le matériel posé (cylindres et serrures certifiés A2P).`,
            },
        ],
        [
            {
                q: `En combien de temps un serrurier arrive-t-il à ${name} ?`,
                a: `Comptez généralement entre 25 et 30 minutes entre votre appel et l'arrivée de l'artisan à ${name}, de jour comme de nuit. Nos équipes sont réparties sur Paris et l'Île-de-France pour tenir ce délai toute l'année, y compris les jours fériés.`,
            },
            {
                q: `Quel est le prix pour changer une serrure à ${name} ?`,
                a: `Le prix d'un changement de serrure à ${name} dépend du modèle installé (standard ou certifié A2P) et du type de porte. Un devis gratuit et sans engagement vous est communiqué par téléphone avant toute intervention.`,
            },
            {
                q: `Un serrurier peut-il intervenir de nuit à ${name} ?`,
                a: `Oui, notre service fonctionne en continu à ${name}, 24h/24 et 7j/7. Un supplément peut s'appliquer en dehors des horaires de journée ; il vous est toujours annoncé avant le déplacement de l'artisan.`,
            },
            {
                q: `Quels documents dois-je présenter au serrurier à ${name} ?`,
                a: `Un justificatif de domicile ou un titre de propriété est systématiquement demandé avant toute ouverture de porte ou changement de serrure à ${name}. Cette vérification protège les occupants légitimes du logement.`,
            },
            {
                q: `Vos interventions à ${name} sont-elles reconnues par les assurances ?`,
                a: `Oui, nos artisans sont agréés par la majorité des assureurs habitation. La facture remise après intervention à ${name} respecte les exigences des compagnies d'assurance, avec certificats sur le matériel A2P posé.`,
            },
        ],
        [
            {
                q: `Sous combien de temps intervenez-vous à ${name} ?`,
                a: `Notre équipe intervient à ${name} en moyenne sous 30 minutes après réception de votre appel, à toute heure du jour ou de la nuit, week-ends et jours fériés compris.`,
            },
            {
                q: `Quel budget prévoir pour une porte claquée à ${name} ?`,
                a: `Une porte simplement claquée (non fermée à clé) est ouverte pour 60€ TTC à ${name}. Si la porte est fermée à clé ou blindée, le tarif est établi sur devis gratuit selon la difficulté technique.`,
            },
            {
                q: `Que faire en cas de cambriolage ou d'effraction à ${name} ?`,
                a: `Contactez-nous immédiatement : nous sécurisons votre porte en urgence à ${name}, 24h/24, avant même le dépôt de plainte. Nos prestations sont conformes aux exigences des assurances pour faciliter votre indemnisation.`,
            },
            {
                q: `Le justificatif de domicile est-il vraiment obligatoire à ${name} ?`,
                a: `Oui, c'est une obligation pour tous nos artisans à ${name} : sans justificatif de domicile ou titre de propriété, aucune ouverture de porte ni changement de serrure n'est réalisé, par sécurité pour l'occupant.`,
            },
            {
                q: `Le matériel posé à ${name} est-il certifié ?`,
                a: `Oui, nous posons exclusivement du matériel certifié A2P à ${name}, reconnu par les assureurs, avec certificat de garantie remis à la fin de l'intervention.`,
            },
        ],
    ];
    return variants[v % variants.length];
}

function getHeroCopy(name, v) {
    const variants = [
        `Serrurier ${name} pas cher : ouverture de porte 60€ TTC, dépannage serrure, blindage, 24h/24. Devis gratuit, artisan agréé assurance, dans le métier depuis 1985.`,
        `Serrurier ${name} : besoin d'une ouverture de porte dès 60€ TTC, d'un changement de serrure ou d'un blindage ? Intervention 24h/24, devis gratuit, artisan agréé assurance, dans le métier depuis 1985.`,
        `Serrurier ${name} disponible 24h/24 pour toute urgence : porte claquée dès 60€ TTC, changement de cylindre, blindage. Devis gratuit au téléphone, agréé assurance.`,
    ];
    return variants[v % variants.length];
}

function getCtaCopy(v) {
    const variants = [
        "Devis gratuit au téléphone — prix annoncé avant l'intervention.",
        "Un artisan vous rappelle en quelques minutes, devis gratuit et sans engagement.",
        "Prix annoncé avant travaux, aucune mauvaise surprise à l'arrivée de l'artisan.",
    ];
    return variants[v % variants.length];
}

export default function LocalityPage({
    name,
    cp,
    kicker,
    crumb,
    introTitle,
    introP1,
    introP2,
    chips,
    others,
    path,
    seoTitle,
    seoDesc,
    image = IMG("tarif-serrurier.webp"),
}) {
    const v = seedIndex(`${cp}-${name}`, 3);
    const guarantees = getGuaranteesCopy(name, v);
    const faq = getFaqCopy(name, v);
    const heroP = getHeroCopy(name, seedIndex(`hero-${cp}-${name}`, 3));
    const ctaSub = getCtaCopy(seedIndex(`cta-${cp}-${name}`, 3));

    const schema = {
        "@context": "https://schema.org",
        "@type": "Locksmith",
        name: `Serrurier ${name}`,
        telephone: "+33625258619",
        url: `https://www.serrurierparis.paris${path}`,
        image: "https://www.serrurierparis.paris/img/logo-serrurierparis.webp",
        openingHours: "Mo-Su 00:00-24:00",
        areaServed: { "@type": "City", name: `${name} (${cp})` },
        address: { "@type": "PostalAddress", addressLocality: name, postalCode: cp, addressCountry: "FR" },
    };

    return (
        <div data-testid="locality-page">
            <SEO title={seoTitle} description={seoDesc} path={path} schema={schema} />

            {/* HERO */}
            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <Link to="/zones-d-intervention" className="transition-colors hover:text-gold">Zones d'intervention</Link>
                        <span>/</span>
                        <span className="text-gold">{crumb}</span>
                    </nav>
                    <SectionLabel dark>{kicker}</SectionLabel>
                    <StatusBadge className="mt-4" />
                    <h1 className="mt-3 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={[`Serrurier ${name}.`]} />
                        <MaskedLines delay={0.45} lines={["Chez vous en 30 min."]} lineClassName="text-gold" />
                    </h1>
                    <FadeUp delay={0.6}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">
                            {heroP}
                        </p>
                        <a
                            href={PHONE_TEL}
                            data-testid="locality-call-button"
                            className="mt-8 inline-flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                        >
                            <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                        </a>
                    </FadeUp>
                </div>
            </section>

            <EditorialMarquee dark={false} />

            {/* INTRO */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
                    <div>
                        <FadeUp>
                            <SectionLabel>{`Votre serrurier — ${cp}`}</SectionLabel>
                            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">{introTitle}</h2>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <p className="mt-6 leading-relaxed text-midnight/70">{introP1}</p>
                            <p className="mt-4 leading-relaxed text-midnight/70">{introP2}</p>
                        </FadeUp>
                        <FadeUp delay={0.18}>
                            <div className="mt-8 flex flex-wrap gap-2" data-testid="locality-chips">
                                {chips.map((q) =>
                                    typeof q === "string" ? (
                                        <span key={q} className="flex items-center gap-1.5 rounded-lg border border-midnight/10 bg-white px-3.5 py-2 text-xs font-semibold text-midnight/70">
                                            <MapPin className="h-3 w-3 text-emergency" /> {q}
                                        </span>
                                    ) : (
                                        <Link
                                            key={q.to}
                                            to={q.to}
                                            className="flex items-center gap-1.5 rounded-lg border border-midnight/10 bg-white px-3.5 py-2 text-xs font-semibold text-midnight/70 transition-colors hover:border-emergency hover:text-emergency"
                                        >
                                            <MapPin className="h-3 w-3 text-emergency" /> {q.label}
                                        </Link>
                                    )
                                )}
                            </div>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.12}>
                        <img
                            src={image}
                            alt={`Serrurier ${name} — intervention ${cp}`}
                            className="aspect-[4/5] w-full rounded-xl object-cover shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]"
                            width="800"
                            height="1000"
                            loading="lazy"
                        />
                    </FadeUp>
                </div>
            </section>

            {/* SERVICES + TARIFS */}
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>{`Prestations — ${name}`}</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            {`Tous nos services à ${name}`}
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {SERVICES.map((s, i) => (
                            <FadeUp key={s.title} delay={(i % 3) * 0.08}>
                                <Link
                                    to={s.slug}
                                    data-testid={`locality-service-${i}`}
                                    className="group flex h-full items-center justify-between border border-midnight/10 bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:border-midnight hover:shadow-[0_28px_60px_-20px_rgba(10,17,40,0.35)]"
                                >
                                    <div>
                                        <h3 className="font-display text-xl font-extrabold tracking-tight">{s.title}</h3>
                                        <p className="mt-1 text-sm font-bold text-emergency">{s.price}</p>
                                    </div>
                                    <ArrowUpRight className="h-5 w-5 text-midnight/30 transition-all duration-300 group-hover:text-emergency" />
                                </Link>
                            </FadeUp>
                        ))}
                    </div>

                    <FadeUp delay={0.1}>
                        <div className="mt-12 divide-y divide-midnight/10 border border-midnight/10 bg-white" data-testid="locality-tarifs">
                            {TARIFS.slice(0, 5).map((t) => (
                                <div key={t.service} className="flex items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-cloud">
                                    <span className="font-semibold">{t.service}</span>
                                    <span className="font-display text-xl font-extrabold text-emergency">{t.price}</span>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* AUTRES SECTEURS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <FadeUp>
                    <SectionLabel>Secteurs voisins</SectionLabel>
                    <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                        Nous intervenons aussi à proximité
                    </h2>
                </FadeUp>
                <div className="mt-8 flex flex-wrap gap-2" data-testid="locality-others">
                    {others.map((o) => (
                        <Link
                            key={o.to}
                            to={o.to}
                            className="rounded-lg border border-midnight/10 bg-white px-4 py-2 text-sm font-semibold text-midnight/70 transition-colors hover:border-emergency hover:text-emergency"
                        >
                            {o.label}
                        </Link>
                    ))}
                </div>
            </section>


            {/* GARANTIES */}
            <section className="bg-cloud py-14 md:py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Nos engagements</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            {`Pourquoi nous choisir à ${name} ?`}
                        </h2>
                        <p className="mt-6 max-w-2xl leading-relaxed text-midnight/70">
                            {guarantees.intro}
                        </p>
                    </FadeUp>
                    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                            { stat: "1985", label: "Dans le métier depuis", detail: `Plus de 40 ans d'expérience en serrurerie — Paris et ${name}` },
                            { stat: "30 min", label: "Délai d'intervention", detail: `Artisan chez vous en 30 min à ${name}, 24h/24` },
                            { stat: "100%", label: "Devis gratuit", detail: "Prix annoncé et validé avant chaque intervention" },
                            { stat: "A2P", label: "Certifié assurance", detail: "Agréé par les assurances, matériel certifié A2P" },
                        ].map((k) => (
                            <FadeUp key={k.stat}>
                                <div className="flex h-full flex-col border border-midnight/10 bg-white p-7">
                                    <span className="font-display text-5xl font-extrabold text-emergency">{k.stat}</span>
                                    <span className="mt-2 text-sm font-bold uppercase tracking-wider text-midnight">{k.label}</span>
                                    <p className="mt-3 text-sm leading-relaxed text-midnight/60">{k.detail}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                    <FadeUp delay={0.12}>
                        <div className="grain mt-10 bg-midnight p-8 text-white md:p-10">
                            <h3 className="font-display text-2xl font-extrabold tracking-tight">
                                {guarantees.honestyTitle}
                            </h3>
                            <p className="mt-4 leading-relaxed text-white/70">
                                {guarantees.honestyP}
                            </p>
                            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                                {[
                                    "Devis écrit remis avant l'intervention",
                                    "SIRET et carte professionnelle présentés sur demande",
                                    "Facture conforme remise après l'intervention",
                                    "Agréé par les assurances habitation",
                                    "Justificatif de domicile obligatoire",
                                    "Prix annoncé au téléphone sans engagement",
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-2 text-sm text-white/75">
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* FAQ */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <FadeUp>
                    <SectionLabel>Questions fréquentes</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        {`FAQ — Serrurier ${name}`}
                    </h2>
                </FadeUp>
                <div className="mt-10 divide-y divide-midnight/10 border border-midnight/10 bg-white" data-testid="locality-faq">
                    {faq.map((item, i) => (
                        <FadeUp key={i} delay={i * 0.04}>
                            <details className="group border-0">
                                <summary className="flex cursor-pointer items-center justify-between gap-4 px-6 py-5 font-display text-base font-bold tracking-tight marker:content-none hover:bg-cloud">
                                    {item.q}
                                    <span className="shrink-0 font-display text-xl text-emergency transition-transform duration-200 group-open:rotate-45">+</span>
                                </summary>
                                <div className="px-6 pb-5 text-sm leading-relaxed text-midnight/70">{item.a}</div>
                            </details>
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="grain bg-midnight py-16 text-white md:py-20">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            {`Urgence à ${name} ? Un artisan arrive.`}
                        </h2>
                        <p className="mt-3 text-white/60">{ctaSub}</p>
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="locality-bottom-call-button"
                        className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                    >
                        <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                    </a>
                </div>
            </section>

            <section className="border-t border-midnight/10 bg-white py-12">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-4 text-sm font-semibold text-midnight/70 sm:px-6 lg:px-8">
                    {["Ouverture de porte sans casse", "Devis gratuit avant intervention", "Artisan agréé assurance", "Justificatif de domicile demandé"].map((item) => (
                        <span key={item} className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-emergency" /> {item}
                        </span>
                    ))}
                </div>
            </section>
        </div>
    );
}
